import 'airbnb-browser-shims';
import { Graph, SizeMap, ColorTree, DisplayNode } from '../types';

const TOTAL_COLOR = '#B0BEC5';
const UNMAPPED_COLOR = '#EEEEEE';
const DEFAULT_COLOR = '#BCAAA4';

/**
 * Search the given directoryColors for the color of this file
 */
export function getBackgroundColor(file: string, directoryColors: ColorTree) {
  const splitFile = file.split('/');
  let color = DEFAULT_COLOR;
  let currentTreeNode = directoryColors;
  // eslint-disable-next-line no-restricted-syntax
  for (const filePart of splitFile) {
    color = currentTreeNode.color || color;
    if (!currentTreeNode.folders[filePart]) {
      break;
    }
    currentTreeNode = currentTreeNode.folders[filePart];
  }
  return color;
}

type NameAndColorInjector = (
  startPoint: string,
  value: number,
  selfValue: number,
  children: DisplayNode[],
) => DisplayNode;

/**
 * Merge the file sizes and dependency information into
 * the proper output format
 */
function getDataForEntryFromFiles(
  startPoint: string,
  fileSizes: SizeMap,
  dependencyGraph: Graph,
  injectNameAndColor: NameAndColorInjector,
  seenFiles: Set<string>,
): DisplayNode | DisplayNode[] {
  seenFiles.add(startPoint);
  const childNames = dependencyGraph[startPoint] || [];
  const children = childNames
    .filter((childName) => {
      if (seenFiles.has(childName)) {
        return false;
      }
      seenFiles.add(childName);
      return true;
    })
    .map((childName) =>
      getDataForEntryFromFiles(
        childName,
        fileSizes,
        dependencyGraph,
        injectNameAndColor,
        seenFiles,
      ),
    )
    .flat();

  // For unknown files, return children directly
  if (/<unknown-file-[0-9]+>/.test(startPoint)) {
    return children;
  }

  const selfValue = fileSizes[startPoint] || 0;
  const value = children.reduce((totalValue, child) => totalValue + child.value, selfValue);
  return injectNameAndColor(startPoint, value, selfValue, children);
}

/**
 * Merge the file sizes and dependency information into
 * the proper output format
 */
function getDataFromFiles(
  entryPoints: string[],
  fileSizes: SizeMap,
  dependencyGraph: Graph,
  _topLevelFolders: string[],
  directoryColors: ColorTree,
  shortenFileName: (name: string) => string,
): DisplayNode {
  const seenFiles = new Set(entryPoints);

  const injectNameAndColor: NameAndColorInjector = (startPoint, value, selfValue, children) => {
    const fileName = shortenFileName(startPoint);
    const backgroundColor = getBackgroundColor(startPoint, directoryColors);
    const valueText = (value / 1024).toFixed(1);
    const selfValueText = (selfValue / 1024).toFixed(1);
    return {
      name: `${fileName} · ${valueText} KiB · self ${selfValueText} KiB`,
      id: startPoint,
      value,
      children,
      backgroundColor,
    };
  };

  const entryChildren = entryPoints
    .map((child) =>
      getDataForEntryFromFiles(child, fileSizes, dependencyGraph, injectNameAndColor, seenFiles),
    )
    .flat();

  const unmappedValue = fileSizes['<unmapped>'];
  const unmapped = {
    name: `Unmapped · ${(unmappedValue / 1024).toFixed(1)} KiB`,
    id: '<unmapped>',
    value: unmappedValue,
    children: [],
    backgroundColor: UNMAPPED_COLOR,
  };

  const children = [...entryChildren, unmapped];

  const value = children.reduce((totalValue, child) => totalValue + child.value, 0);

  return {
    name: `Total · ${(value / 1024).toFixed(1)} KiB`,
    id: '<total>',
    value,
    children,
    backgroundColor: TOTAL_COLOR,
  };
}

export default getDataFromFiles;
